/**
 * Iframe Keyboard Fix Utility
 *
 * Enhanced keyboard handling specifically for iframe environments where
 * the visualViewport API doesn't work reliably.
 *
 * Features:
 * - Iframe detection
 * - Focus-based keyboard detection (not viewport-based)
 * - Keyboard height caching
 * - User scroll detection (don't fight user)
 * - Debounced scroll logic
 * - Fallback scroll methods
 * - CSS class management for styling
 *
 * Based on recommendations from expert advisory board:
 * - Sarah Chen (iOS/Safari Expert)
 * - Marcus Thompson (Hybrid App Expert)
 * - David Park (UX Engineer)
 */

// Debug tracing — silent in production, visible during local development.
const dbg = (...args) => {
  if (process.env.NODE_ENV !== 'production') console.log(...args);
};

// ==========================================
// STATE MANAGEMENT
// ==========================================

let activeElement = null;
let userHasScrolled = false;
let lastScrollTime = 0;
let scrollTimeout = null;
let initialViewportHeight = null;
let isKeyboardVisible = false;

// ==========================================
// DETECTION UTILITIES
// ==========================================

/**
 * Detect if running inside an iframe
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // Cross-origin iframe - we can't access parent
    return true;
  }
};

/**
 * Detect iOS device
 */
export const isIOS = () => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      navigator.platform
    ) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

/**
 * Detect Android device
 */
export const isAndroid = () => {
  return /Android/i.test(navigator.userAgent);
};

/**
 * Detect if this is a mobile device
 */
export const isMobile = () => {
  return isIOS() || isAndroid() || window.innerWidth < 768;
};

// ==========================================
// KEYBOARD HEIGHT CACHING (Marcus's recommendation)
// ==========================================

const KEYBOARD_HEIGHT_KEY = 'iframe_keyboard_height';
const DEFAULT_KEYBOARD_HEIGHT = 320;

/**
 * Get cached keyboard height or default
 */
export const getKeyboardHeight = () => {
  try {
    const cached = localStorage.getItem(KEYBOARD_HEIGHT_KEY);
    if (cached) {
      const height = parseInt(cached, 10);
      if (height > 100 && height < 600) {
        return height;
      }
    }
  } catch (e) {
    // localStorage not available
  }
  return DEFAULT_KEYBOARD_HEIGHT;
};

/**
 * Update cached keyboard height
 */
export const updateKeyboardHeight = (height) => {
  try {
    if (height > 100 && height < 600) {
      localStorage.setItem(KEYBOARD_HEIGHT_KEY, height.toString());
    }
  } catch (e) {
    // localStorage not available
  }
};

/**
 * Try to estimate keyboard height from viewport changes
 */
const estimateKeyboardHeight = () => {
  if (initialViewportHeight && window.visualViewport) {
    const currentHeight = window.visualViewport.height;
    const diff = initialViewportHeight - currentHeight;
    if (diff > 100) {
      updateKeyboardHeight(diff);
      return diff;
    }
  }
  return getKeyboardHeight();
};

// ==========================================
// SCROLL UTILITIES (Sarah's recommendation)
// ==========================================

/**
 * Primary scroll method using scrollIntoView
 */
const scrollIntoViewMethod = (element, options = {}) => {
  if (!element) return;

  element.scrollIntoView({
    behavior: options.smooth !== false ? 'smooth' : 'auto',
    block: options.block || 'center',
    inline: 'nearest',
  });
};

/**
 * Fallback scroll method using window.scrollTo
 * (Sarah's recommendation - works when scrollIntoView is intercepted)
 */
const fallbackScrollMethod = (element) => {
  if (!element) return;

  const rect = element.getBoundingClientRect();
  const scrollTarget = window.pageYOffset + rect.top - window.innerHeight / 2;

  window.scrollTo({
    top: Math.max(0, scrollTarget),
    behavior: 'smooth',
  });
};

/**
 * Aggressive scroll with multiple attempts
 */
const aggressiveScroll = (element) => {
  if (!element) return;

  // Immediate scroll
  scrollIntoViewMethod(element);

  // After keyboard animation (300ms)
  setTimeout(() => {
    scrollIntoViewMethod(element);

    // Add extra scroll margin for keyboard
    window.scrollBy({ top: 50, behavior: 'smooth' });
  }, 350);

  // Final safety scroll using fallback method
  setTimeout(() => {
    fallbackScrollMethod(element);
  }, 600);

  // Removed blur/focus cycle to prevent infinite loop
  // CSS transform now handles keyboard avoidance via media queries
};

// ==========================================
// USER SCROLL DETECTION (David's recommendation)
// ==========================================

/**
 * Track user scroll events
 */
const setupUserScrollDetection = () => {
  document.addEventListener(
    'scroll',
    () => {
      lastScrollTime = Date.now();
      userHasScrolled = true;
    },
    { passive: true }
  );

  // Also track touch events as potential user interaction
  document.addEventListener(
    'touchmove',
    () => {
      lastScrollTime = Date.now();
    },
    { passive: true }
  );
};

/**
 * Check if user has scrolled recently
 */
const hasUserScrolledRecently = (thresholdMs = 2000) => {
  return userHasScrolled && Date.now() - lastScrollTime < thresholdMs;
};

// ==========================================
// DEBOUNCING (David's recommendation)
// ==========================================

/**
 * Debounced scroll to element
 */
const debouncedScroll = (element, delayMs = 150) => {
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    scrollToActiveElement(element);
  }, delayMs);
};

// ==========================================
// MAIN SCROLL LOGIC
// ==========================================

/**
 * Scroll to active element with all enhancements
 */
const scrollToActiveElement = (element) => {
  const targetElement = element || activeElement;
  if (!targetElement) return;

  // Don't fight user scroll (David's recommendation)
  if (hasUserScrolledRecently()) {
    dbg('[IframeKeyboardFix] Respecting user scroll, not auto-scrolling');
    return;
  }

  const inIframe = isInIframe();

  if (inIframe || isIOS()) {
    // In iframe or iOS: use aggressive scroll (skip viewport checks)
    dbg('[IframeKeyboardFix] Using aggressive scroll for iframe/iOS');
    aggressiveScroll(targetElement);
  } else {
    // Normal environment: use standard scroll
    scrollIntoViewMethod(targetElement);
  }
};

// ==========================================
// CSS CLASS MANAGEMENT
// ==========================================

/**
 * Add keyboard-open class to document
 */
const setKeyboardOpen = (isOpen) => {
  isKeyboardVisible = isOpen;

  if (isOpen) {
    document.documentElement.classList.add('keyboard-open');
    document.body.classList.add('keyboard-open');

    // Set CSS custom property for keyboard height
    const keyboardHeight = getKeyboardHeight();
    document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
  } else {
    document.documentElement.classList.remove('keyboard-open');
    document.body.classList.remove('keyboard-open');
    document.documentElement.style.removeProperty('--keyboard-height');
  }
};

// ==========================================
// FOCUS EVENT HANDLERS
// ==========================================

/**
 * Handle focus in event
 */
const handleFocusIn = (event) => {
  const target = event.target;

  // Check if it's an input element
  if (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable
  ) {
    activeElement = target;

    // Add focused class
    target.classList.add('iframe-input-focused');

    // Set keyboard open state
    setKeyboardOpen(true);

    // Reset user scroll state on new focus
    userHasScrolled = false;

    // Use debounced scroll
    debouncedScroll(target, 150);

    dbg('[IframeKeyboardFix] Focus detected, scrolling to input');
  }
};

/**
 * Handle focus out event
 */
const handleFocusOut = (event) => {
  const target = event.target;

  // Remove focused class
  target.classList.remove('iframe-input-focused');

  // Delay keyboard close to handle focus transitions
  setTimeout(() => {
    const currentActive = document.activeElement;

    if (
      !currentActive ||
      !['INPUT', 'TEXTAREA'].includes(currentActive.tagName)
    ) {
      activeElement = null;
      setKeyboardOpen(false);
      dbg('[IframeKeyboardFix] Focus lost, keyboard closed');
    }
  }, 100);
};

// ==========================================
// PARENT APP COMMUNICATION (Marcus's recommendation)
// ==========================================

/**
 * Listen for potential keyboard events from parent app
 */
const setupParentAppListener = () => {
  window.addEventListener('message', (event) => {
    // Check for keyboard events from parent
    const data = event.data;

    if (!data || typeof data !== 'object') return;

    if (data.type === 'keyboardWillShow' || data.type === 'keyboardDidShow') {
      dbg('[IframeKeyboardFix] Parent app keyboard show event received');
      setKeyboardOpen(true);

      if (data.height) {
        updateKeyboardHeight(data.height);
      }

      if (activeElement) {
        debouncedScroll(activeElement, 100);
      }
    }

    if (data.type === 'keyboardWillHide' || data.type === 'keyboardDidHide') {
      dbg('[IframeKeyboardFix] Parent app keyboard hide event received');
      setKeyboardOpen(false);
    }
  });
};

// ==========================================
// VIEWPORT MONITORING
// ==========================================

/**
 * Monitor viewport changes as fallback detection
 */
const setupViewportMonitoring = () => {
  // Store initial height
  initialViewportHeight = window.visualViewport?.height || window.innerHeight;

  // CSS custom property for viewport
  const setViewportProperty = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    if (!window.__INITIAL_VH__) {
      window.__INITIAL_VH__ = vh;
      document.documentElement.style.setProperty('--initial-vh', `${vh}px`);
    }
  };

  setViewportProperty();
  window.addEventListener('resize', setViewportProperty);

  // Set initial visual viewport height for CSS-based container resizing
  const initialVVHeight = window.visualViewport?.height || window.innerHeight;
  document.documentElement.style.setProperty('--visual-viewport-height', `${initialVVHeight}px`);

  // Visual viewport monitoring
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', () => {
      setViewportProperty();

      // Always update visual viewport height CSS variable for dynamic container sizing
      const currentHeight = window.visualViewport.height;
      document.documentElement.style.setProperty('--visual-viewport-height', `${currentHeight}px`);

      // Detect keyboard state from viewport change
      const heightDiff = initialViewportHeight - currentHeight;
      if (heightDiff > 100 && activeElement) {
        // Keyboard opening
        estimateKeyboardHeight();
        debouncedScroll(activeElement, 100);
      } else if (heightDiff < 50 && isKeyboardVisible) {
        // Keyboard closing - viewport returned to near initial height
        setKeyboardOpen(false);
      }
    });
  }
};

// ==========================================
// INITIALIZATION
// ==========================================

/**
 * Initialize all iframe keyboard fixes
 */
export const initIframeKeyboardFix = (options = {}) => {
  const { enableParentListener = true } = options;

  dbg('[IframeKeyboardFix] Initializing...', {
    isIframe: isInIframe(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isMobile: isMobile(),
  });

  // Set up user scroll detection
  setupUserScrollDetection();

  // Set up viewport monitoring
  setupViewportMonitoring();

  // Set up focus event listeners
  document.addEventListener('focusin', handleFocusIn);
  document.addEventListener('focusout', handleFocusOut);

  // Set up parent app communication
  if (enableParentListener) {
    setupParentAppListener();
  }

  dbg('[IframeKeyboardFix] Initialized successfully');

  // Return cleanup function
  return () => {
    document.removeEventListener('focusin', handleFocusIn);
    document.removeEventListener('focusout', handleFocusOut);
    setKeyboardOpen(false);
  };
};

// ==========================================
// PUBLIC API
// ==========================================

/**
 * Manually trigger scroll to input
 */
export const scrollToInput = (element) => {
  const targetElement = element || activeElement;
  if (targetElement) {
    aggressiveScroll(targetElement);
  }
};

/**
 * Get current keyboard state
 */
export const getKeyboardState = () => ({
  isVisible: isKeyboardVisible,
  isIframe: isInIframe(),
  isIOS: isIOS(),
  estimatedHeight: getKeyboardHeight(),
  activeElement: activeElement?.tagName || null,
  userScrolledRecently: hasUserScrolledRecently(),
});

/**
 * Force keyboard open state (for testing)
 */
export const forceKeyboardState = (isOpen) => {
  setKeyboardOpen(isOpen);
};

export default initIframeKeyboardFix;
